import { useMemo } from 'react';

import { humanReadableStringFromTokenBigInt, parseFloatGuarded } from '@/utils';
import { getSwapAmountLimitByOrder, parseSwapLimits } from '@/utils/swapLimits';

import { Currency } from '../../constants';
import { useTokenUsdPrices } from '../useTokenUsdPrices';

export const useSwapLimitsSatisfied = (fromToken: Currency, toToken: Currency, fromAmount: bigint): boolean => {
  const { getTokenUsdPrice } = useTokenUsdPrices();

  const isLimitsSatisfied = useMemo(() => {
    const minSwapAmounts = parseSwapLimits(import.meta.env.VITE_BETA_MIN_SWAP_AMOUNT_USD as string);
    const maxSwapAmounts = parseSwapLimits(import.meta.env.VITE_BETA_MAX_SWAP_AMOUNT_USD as string);
    const minSwapAmountLimit = getSwapAmountLimitByOrder(minSwapAmounts, fromToken, toToken);
    const maxSwapAmountLimit = getSwapAmountLimitByOrder(maxSwapAmounts, fromToken, toToken);

    if (minSwapAmountLimit > 0 || maxSwapAmountLimit > 0) {
      const c1AmountUsdRaw = BigInt(Math.round(Number(fromAmount) * (getTokenUsdPrice(fromToken) || 0)));
      const c1UsdHuman = parseFloatGuarded(humanReadableStringFromTokenBigInt(c1AmountUsdRaw, fromToken)).toNumber();

      const isC1AmountInLimitationRange =
        c1UsdHuman >= (minSwapAmountLimit || 0) * 0.999 && c1UsdHuman <= (maxSwapAmountLimit || Infinity) * 1.001;

      if (!isC1AmountInLimitationRange) {
        return false;
      }
    }

    return true;
  }, [getTokenUsdPrice, fromToken, toToken, fromAmount]);

  return isLimitsSatisfied;
};
